import { uniqBy } from "lodash";
import moment from "moment";
import {
  convertDatesForFilterPayload,
  reportTransformTimeForAPI,
  reportsCommonFormInitialValues
} from "./commonReportHelpers";

type AnyRecord = Record<string, any>;

const extractTimeForForm = (
  value: unknown,
  fallback: string | null | undefined
): string | null | undefined => {
  if (value == null || value === "") {
    return fallback;
  }

  if (typeof value === "string" && value.includes("T")) {
    return moment(value).format("HH:mm");
  }

  if (typeof value === "string" && value.includes(":")) {
    // "00:00:00" or "00:00"
    return value.slice(0, 5);
  }

  return fallback;
};

const normalizeDaysOfWeekForForm = (
  daysOfWeek: unknown,
  fallback: string[] = ["0", "1", "2", "3", "4", "5", "6"]
): string[] => {
  if (!Array.isArray(daysOfWeek) || daysOfWeek.length === 0) {
    // Empty array from API means all days selected
    return fallback;
  }

  return daysOfWeek.map(String);
};

/**
 * Map persisted Redux reportsFilterData into Formik form values.
 * assignedToAllObjects is derived from whether object IDs are present.
 * Does not copy calendar dates or report metadata into the form.
 */
export const mapReportsFilterDataToFormValues = (
  filterData: AnyRecord | null | undefined,
  baseInitialValues: AnyRecord = reportsCommonFormInitialValues
): AnyRecord => {
  if (!filterData || Object.keys(filterData).length === 0) {
    return { ...baseInitialValues };
  }

  const {
    startDate: _startDate,
    endDate: _endDate,
    reportType: _reportType,
    startedWithoutIgnition: _startedWithoutIgnition,
    shouldNotResolveAddresses: _shouldNotResolveAddresses,
    hasExceededLimit: _hasExceededLimit,
    startTime: _startTime,
    endTime: _endTime,
    object: _object,
    objectIds: _objectIds,
    assignedToAllObjects: _assignedToAllObjects,
    ...safeFilterData
  } = filterData;

  const objectField =
    Array.isArray(filterData.objectIds) && !Array.isArray(filterData.object)
      ? "objectIds"
      : "object";
  const objectIds = (filterData[objectField] ??
    filterData.object ??
    []) as any[];
  const hasSpecificObjects = Array.isArray(objectIds) && objectIds.length > 0;
  const daysOfWeek = normalizeDaysOfWeekForForm(
    filterData.daysOfWeek,
    baseInitialValues.daysOfWeek
  );
  const hasDaysOfWeek = Array.isArray(daysOfWeek) && daysOfWeek.length > 0;
  const defaultStartTime = hasDaysOfWeek
    ? (baseInitialValues.startTime ?? "00:00")
    : baseInitialValues.startTime;
  const defaultEndTime = hasDaysOfWeek
    ? (baseInitialValues.endTime ?? "23:59")
    : baseInitialValues.endTime;

  return {
    ...baseInitialValues,
    ...safeFilterData,
    objectType: filterData.objectType ?? baseInitialValues.objectType ?? [],
    group: filterData.group ?? baseInitialValues.group ?? [],
    object: hasSpecificObjects ? objectIds : [],
    ...(objectField === "objectIds"
      ? { objectIds: hasSpecificObjects ? objectIds : [] }
      : {}),
    key: filterData.key ?? baseInitialValues.key ?? [],
    daysOfWeek,
    startTime: extractTimeForForm(filterData.startTime, defaultStartTime),
    endTime: extractTimeForForm(filterData.endTime, defaultEndTime),
    rideModes: filterData.rideModes ?? baseInitialValues.rideModes ?? [],
    assignedToAllObjects: !hasSpecificObjects
  };
};

export type BuildReportFilterPayloadArgs = {
  values: AnyRecord;
  dateValues: { startDate: Date; endDate: Date };
  reportType: number;
  apiPayload?: AnyRecord | null;
  extra?: AnyRecord;
  /** When true, use convertDatesForFilterPayload (same-day 00:00-23:59). Default true. */
  normalizeDates?: boolean;
  startDateOverride?: string;
  endDateOverride?: string;
};

/**
 * Build a consistent API payload for report filters.
 * When assignedToAllObjects is true, object / objectIds is always [].
 */
export const buildReportFilterPayload = ({
  values,
  dateValues,
  reportType,
  apiPayload = null,
  extra = {},
  normalizeDates = true,
  startDateOverride,
  endDateOverride
}: BuildReportFilterPayloadArgs): AnyRecord => {
  let startDate = startDateOverride;
  let endDate = endDateOverride;

  if (!startDate || !endDate) {
    if (normalizeDates) {
      const formatted = convertDatesForFilterPayload(dateValues);
      startDate = startDate ?? formatted.formattedStartDate;
      endDate = endDate ?? formatted.formattedEndDate;
    }
  }

  const startDateObj = new Date(dateValues.startDate);
  const endDateObj = new Date(dateValues.endDate);
  const objectIds =
    values.assignedToAllObjects === true
      ? []
      : (values.objectIds ?? values.object ?? []);

  const daysOfWeek = Array.isArray(values.daysOfWeek)
    ? values.daysOfWeek.length === 7
      ? []
      : values.daysOfWeek.map(Number)
    : undefined;

  const toTimeWithSeconds = (time: unknown, fallback: string) => {
    const raw = typeof time === "string" && time.length ? time : fallback;
    return raw.length === 5 ? `${raw}:00` : raw;
  };

  const timeFields =
    values.startTime != null || values.endTime != null
      ? {
          startTime: reportTransformTimeForAPI(
            toTimeWithSeconds(values.startTime, "00:00"),
            startDateObj,
            false
          ),
          endTime: reportTransformTimeForAPI(
            toTimeWithSeconds(values.endTime, "23:59"),
            endDateObj,
            true,
            startDateObj
          )
        }
      : {};

  const objectFieldPayload =
    reportType === 8 ? { objectIds } : { object: objectIds };

  const {
    object: _object,
    objectIds: _objectIds,
    assignedToAllObjects: _assignedToAllObjects,
    startDate: _valuesStartDate,
    endDate: _valuesEndDate,
    startTime: _valuesStartTime,
    endTime: _valuesEndTime,
    reportType: _valuesReportType,
    startedWithoutIgnition: _valuesStartedWithoutIgnition,
    shouldNotResolveAddresses: _valuesShouldNotResolveAddresses,
    ...restValues
  } = values;

  const sanitizedApiPayload = { ...(apiPayload ?? {}) };
  delete sanitizedApiPayload.assignedToAllObjects;
  delete sanitizedApiPayload.startDate;
  delete sanitizedApiPayload.endDate;
  delete sanitizedApiPayload.startTime;
  delete sanitizedApiPayload.endTime;
  delete sanitizedApiPayload.reportType;
  if (reportType === 8) {
    delete sanitizedApiPayload.object;
  } else {
    delete sanitizedApiPayload.objectIds;
  }

  // Calendar dates and times must win over any stale keys leaked into form values.
  return {
    ...sanitizedApiPayload,
    ...restValues,
    ...objectFieldPayload,
    reportType,
    ...(daysOfWeek !== undefined ? { daysOfWeek } : {}),
    startDate,
    endDate,
    ...timeFields,
    ...extra
  };
};

export type MergeReportObjectOptionsArgs = {
  selected?: any[];
  queried?: any[];
  preloaded?: any[];
};

/** Merge selected + queried + preloaded objects, deduped by uniqueId. */
export const mergeReportObjectOptions = ({
  selected = [],
  queried = [],
  preloaded = []
}: MergeReportObjectOptionsArgs): any[] => {
  return uniqBy(
    [...(selected || []), ...(queried || []), ...(preloaded || [])].filter(
      Boolean
    ),
    "uniqueId"
  );
};

/** Defaults used when the user resets filters in a modal. */
export const getResetFilterFormValues = (
  baseInitialValues: AnyRecord = reportsCommonFormInitialValues
): AnyRecord => {
  return {
    ...baseInitialValues,
    objectType: [],
    group: [],
    object: [],
    ...(Object.prototype.hasOwnProperty.call(baseInitialValues, "objectIds")
      ? { objectIds: [] }
      : {}),
    key: [],
    daysOfWeek: baseInitialValues.daysOfWeek ?? [
      "0",
      "1",
      "2",
      "3",
      "4",
      "5",
      "6"
    ],
    startTime: baseInitialValues.startTime ?? "00:00",
    endTime: baseInitialValues.endTime ?? "23:59",
    rideModes: baseInitialValues.rideModes ?? [],
    assignedToAllObjects: true
  };
};
